// Redux
import { Reducer } from 'redux';
import produce, { Draft } from 'immer';
import { get, uniqBy } from 'lodash';

// Types
import { AlertResponse } from 'app/modules/alerts/types';

// Models
import { AlertsActionTypes, AlertsState } from 'app/modules/alerts/models';
import { EventActionTypes } from 'app/shared/eventsOld/models';

// Actions
import { AlertsActions } from 'app/modules/alerts/actions';
import { EventsActions } from 'app/shared/eventsOld/actions';

// Constants
import { INITIAL_ALERT } from 'app/modules/alerts/constants';

// Utils
import deepFreeze from 'app/shared/utils/deepFreeze';

const initialState: Readonly<AlertsState> = {
  alerts: [],
  alert: { ...INITIAL_ALERT },
  histogram: {
    histogram_data: {
      in: [],
      out: [],
    },
    transaction_codes: [],
  },
};

// to ensure initialState is readonly
deepFreeze(initialState);

const reducer: Reducer<AlertsState> = (
  state = initialState,
  action: AlertsActions | EventsActions,
) => {
  return produce(state, (draft: Draft<AlertsState>) => {
    // When making some change, it may apply to the primary alert, sidebar
    // alert, or some row in a table, this method groups it all together.
    // Can be used for a single alert or a list of updated alerts
    const modifyAlerts = (updatedValues: AlertResponse | AlertResponse[]) => {
      const newAlerts = {};
      if (Array.isArray(updatedValues)) {
        updatedValues.forEach((newAlert) => {
          newAlerts[newAlert.id] = newAlert;
        });
      } else {
        newAlerts[updatedValues.id] = updatedValues;
      }

      // Updating alert details
      if (newAlerts[draft.alert.id]) {
        draft.alert = {
          ...draft.alert,
          ...newAlerts[draft.alert.id],
        };
      }

      // Updating table alerts
      draft.alerts = draft.alerts.map((alert) => {
        if (newAlerts[alert.id]) {
          return {
            ...alert,
            ...newAlerts[alert.id],
          };
        }
        return alert;
      });
    };

    switch (action.type) {
      case AlertsActionTypes.RETRIEVE_ALERT_SUCCESS:
      case AlertsActionTypes.RETRIEVE_ALERT_DETAILS_SUCCESS:
        draft.alert = action.payload;
        modifyAlerts(action.payload);
        return;

      case AlertsActionTypes.RETRIEVE_ALERT_TRANSACTIONS_SUCCESS:
        draft.alert.events = action.payload;
        return;

      case AlertsActionTypes.CLEAR_ALERT_ENTITIES:
        draft.alert.entities = initialState.alert.entities;
        return;

      case AlertsActionTypes.RETRIEVE_ALERT_ENTITIES_SUCCESS:
        draft.alert.entities = action.payload.entities;
        return;

      case AlertsActionTypes.RETRIEVE_ALERTS_SUCCESS:
        draft.alerts = uniqBy(
          [
            ...(get(action.payload, 'append') ? draft.alerts : []),
            ...action.payload.alerts,
          ],
          (actionVar) => actionVar.id || JSON.stringify(actionVar),
        );
        return;

      case AlertsActionTypes.CHANGE_ALERT_COMPONENT_STATUS_SUCCESS:
        modifyAlerts(action.payload);
        return;

      case EventActionTypes.EDIT_EVENT_SUCCESS:
        draft.alert.events = (draft.alert.events || []).map((event) => {
          if (event.id === action.payload.id) {
            return action.payload;
          }
          return event;
        });
        return;

      case AlertsActionTypes.EDIT_ALERT_SUCCESS:
        // The API returns a weird partial object that contains alert
        // values. Since `modifyAlerts` spreads the original alert
        // in addition to the new values, this is fine for now
        modifyAlerts(action.payload as any);
        return;

      case AlertsActionTypes.ADD_DOCUMENTS_SUCCESS:
        draft.alert.attachments = action.payload.attachments;
        return;

      case AlertsActionTypes.DELETE_DOCUMENT_SUCCESS:
        draft.alert.attachments = action.payload.attachments;
        return;

      case AlertsActionTypes.RETRIEVE_ALERT_HISTOGRAM_SUCCESS:
        draft.histogram = action.payload;
        return;

      case AlertsActionTypes.RETRIEVE_ALERT_ACTION_EVENTS_SUCCESS:
        draft.alert.action_events = action.payload;
        return;

      default:
        return;
    }
  });
};

export { reducer as alertsReducer, initialState };
